import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import {
  getPatientExerciseAPI,
  addPatientExerciseAPI,
} from "../../../../../../services/outpatient.services";
import moment from "moment";

export const useExerciseData = (admissionId, onValuesChange) => {
  const [prescribedData, setPrescribedData] = useState([]);
  const shouldNotifyDirtyRef = useRef(false);

  const isDuplicateExerciseName = (name) => {
    if (!name) return false;
    return prescribedData.some(
      (exercise) =>
        exercise.name?.trim().toLowerCase() === name.trim().toLowerCase()
    );
  };

  const fetchExercises = async () => {
    if (!admissionId) return;

    try {
      const res = await getPatientExerciseAPI(admissionId);
      const dataArray = Array.isArray(res?.data)
        ? res.data
        : res?.data
        ? [res.data]
        : [];

      const formattedData = dataArray.map((item) => {
        const filePath =
          Array.isArray(item.file) && item.file.length > 0
            ? item.file[0].path
            : item.file?.path || null;

        const fileType =
          Array.isArray(item.file) && item.file.length > 0
            ? item.file[0].type
            : item.file?.type || null;

        return {
          ...item,
          localId: item.id,
          startDate: item.start_date,
          duration: item.duration,
          name: item.name,
          type: item.exercise_type,
          link: item.others_link || item.s3_path?.url || filePath || null,
          s3Path: item.s3_path?.url
            ? { url: item.s3_path.url, type: item.s3_path.type || fileType }
            : filePath
            ? { path: filePath, type: fileType }
            : null,
        };
      });

      setPrescribedData(Array.isArray(formattedData) ? formattedData : []);
    } catch (err) {
      console.error("Failed to fetch prescribed exercises:", err);
      message.error("Failed to load prescribed exercises");
      setPrescribedData([]);
    }
  };

  const addExercise = async (payload) => {
    try {
      await addPatientExerciseAPI(payload);
      message.success("Exercise added successfully!");
      // Only mark the EMR "dirty" after an explicit user action (add exercise).
      // Avoid marking dirty on initial fetch / background refresh.
      shouldNotifyDirtyRef.current = true;
      await fetchExercises();
      return true;
    } catch (err) {
      console.error(err);
      message.error("Failed to add exercise");
      return false;
    }
  };

  useEffect(() => {
    fetchExercises();
  }, [admissionId]);

  useEffect(() => {
    if (!onValuesChange) return;

    const shouldNotifyDirty = shouldNotifyDirtyRef.current;
    onValuesChange("exercise", prescribedData, {
      // Hydrate parent state without marking form dirty on initial fetch/refresh.
      hydrateOnly: !shouldNotifyDirty,
    });

    if (shouldNotifyDirty) {
      shouldNotifyDirtyRef.current = false;
    }
  }, [prescribedData]);

  return {
    prescribedData,
    setPrescribedData,
    fetchExercises,
    addExercise,
    isDuplicateExerciseName,
  };
};
